/**
 * Lightweight telemetry helper.
 *
 * In-memory event log for local/dev inspection, plus a first-party
 * usage-analytics client that POSTs to the configured Usage API endpoint.
 * The usage client only ever sends { app_key, feature, action, user_id? }.
 */

export type TelemetryEvent = {
  name: string
  ts: number
  data?: Record<string, unknown>
}

const queue: TelemetryEvent[] = []
const MAX_EVENTS = 200

export function recordEvent(name: string, data: Record<string, unknown> = {}): TelemetryEvent {
  const event: TelemetryEvent = {
    name,
    ts: Date.now(),
    data
  }

  queue.push(event)
  if (queue.length > MAX_EVENTS) {
    queue.shift()
  }

  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.info('[telemetry]', event)
  }

  // Also expose for manual inspection in devtools
  const w = window as unknown as { __lytTelemetry?: TelemetryEvent[] }
  w.__lytTelemetry = queue

  return event
}

export function trackTTFI(page: string): void {
  const start = performance.now()
  const mark = () => {
    recordEvent('ttfi', { page, ms: Math.round(performance.now() - start) })
    window.removeEventListener('pointerdown', mark, true)
    window.removeEventListener('keydown', mark, true)
  }

  window.addEventListener('pointerdown', mark, { once: true, capture: true })
  window.addEventListener('keydown', mark, { once: true, capture: true })
}

export function getTelemetryEvents(): TelemetryEvent[] {
  return [...queue]
}

// ----------------------------------------------------------------------------
// Usage analytics
// ----------------------------------------------------------------------------

export type UsageAction = 'view' | 'click' | 'submit' | 'change'

export interface UsageRecordRequest {
  app_key: string
  feature: string
  action: UsageAction
  user_id?: string
}

interface UsageConfig {
  getUserId?: () => string | null | undefined
}

interface UsageModuleState {
  appKey: string
  recordUrl: string
  getUserId: () => string | null | undefined
  warnedDisabled: boolean
}

const MAX_PENDING = 50
const pending: UsageRecordRequest[] = []

let state: UsageModuleState | null = null

function ensureState(config: UsageConfig): UsageModuleState | null {
  if (state) return state

  const recordUrl = (import.meta.env.VITE_USAGE_RECORD_URL ?? '').trim()
  const appKey = (import.meta.env.VITE_USAGE_APP_KEY ?? '').trim()

  if (!recordUrl || !appKey) {
    return null
  }

  state = {
    appKey,
    recordUrl,
    getUserId: config.getUserId ?? (() => undefined),
    warnedDisabled: false
  }
  return state
}

function warnDisabledOnce(): void {
  if (!import.meta.env.DEV) return
  if (state) {
    if (state.warnedDisabled) return
    state.warnedDisabled = true
    return
  }
  // eslint-disable-next-line no-console
  console.info(
    '[usage] usage tracking disabled: VITE_USAGE_RECORD_URL and/or VITE_USAGE_APP_KEY not configured.'
  )
}

function buildPayload(
  feature: string,
  action: UsageAction,
  userId: string | null | undefined
): UsageRecordRequest {
  const payload: UsageRecordRequest = {
    app_key: state!.appKey,
    feature,
    action
  }
  if (typeof userId === 'string' && userId.length > 0) {
    payload.user_id = userId
  }
  return payload
}

function enqueue(payload: UsageRecordRequest): void {
  pending.push(payload)
  if (pending.length > MAX_PENDING) {
    pending.splice(0, pending.length - MAX_PENDING)
  }
}

function send(payload: UsageRecordRequest): void {
  if (!state) {
    warnDisabledOnce()
    return
  }
  let body: string
  try {
    body = JSON.stringify(payload)
  } catch {
    return
  }
  try {
    void fetch(state.recordUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true
    }).catch(() => {
      // Swallow network errors; usage tracking must never block the UI.
    })
  } catch {
    // Defensive: fetch is not available or threw synchronously.
  }
}

export function configureUsageTracking(options: UsageConfig = {}): void {
  ensureState(options)
  if (!state && import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.info(
      '[usage] configureUsageTracking called without VITE_USAGE_RECORD_URL / VITE_USAGE_APP_KEY; network sends disabled.'
    )
  }
}

export function recordUsage(feature: string, action: UsageAction): void {
  if (!state) {
    warnDisabledOnce()
    return
  }
  if (!feature) return
  const userId = state.getUserId()
  const payload = buildPayload(feature, action, userId)
  enqueue(payload)
  send(payload)
}

export function trackUsagePage(feature: string): void {
  recordUsage(feature, 'view')
}

export function trackUsageAction(
  feature: string,
  action: Exclude<UsageAction, 'view'>
): void {
  recordUsage(feature, action)
}

// ----------------------------------------------------------------------------
// Global delegated action tracking
// ----------------------------------------------------------------------------

const DEDUPE_WINDOW_MS = 250
const lastFire = new Map<string, number>()

function shouldFire(key: string): boolean {
  const now = performance.now()
  const previous = lastFire.get(key)
  if (previous !== undefined && now - previous < DEDUPE_WINDOW_MS) {
    return false
  }
  lastFire.set(key, now)
  return true
}

function getStableLabel(el: Element | null): string {
  let current: Element | null = el
  while (current && current instanceof Element) {
    if (current instanceof HTMLElement) {
      const dataFeature = current.getAttribute('data-usage-feature')
      if (dataFeature) return dataFeature.replace(/\s+/g, ' ').trim()
    }
    const aria = current.getAttribute('aria-label')
    if (aria) return aria.replace(/\s+/g, ' ').trim()
    const text = current.textContent?.replace(/\s+/g, ' ').trim() ?? ''
    if (text) return text
    current = current.parentElement
  }
  return ''
}

function resolveClickTarget(target: EventTarget | null): Element | null {
  if (!(target instanceof Node)) return null
  let current: Element | null = target instanceof Element ? target : null
  while (current && current instanceof Element) {
    if (current instanceof HTMLElement) {
      if (current.getAttribute('data-usage-feature')) return current
      if (current.getAttribute('aria-label')) return current
    }
    if (
      current instanceof HTMLButtonElement ||
      current instanceof HTMLAnchorElement ||
      current instanceof HTMLInputElement ||
      current instanceof HTMLSelectElement ||
      current instanceof HTMLTextAreaElement
    ) {
      return current
    }
    const role = current.getAttribute('role')
    if (role === 'button' || role === 'link') return current
    current = current.parentElement
  }
  return null
}

function handleClick(event: Event, pageFeature: string): void {
  const anchor = resolveClickTarget(event.target)
  if (!anchor) return
  const label = getStableLabel(anchor) || pageFeature.replace(/^page:/, '')
  const feature = `btn:${label}`
  if (!shouldFire(`${feature}|click`)) return
  trackUsageAction(feature, 'click')
}

function handleSubmit(event: Event, pageFeature: string): void {
  let form: HTMLFormElement | null = null
  const target = event.target
  if (target instanceof HTMLFormElement) {
    form = target
  } else if (target instanceof Element) {
    form = target.closest('form')
  }
  let label = ''
  if (form) {
    label =
      getStableLabel(form) ||
      form.getAttribute('name')?.replace(/\s+/g, ' ').trim() ||
      form.getAttribute('id')?.replace(/\s+/g, ' ').trim() ||
      ''
  }
  if (!label) {
    label = pageFeature.replace(/^page:/, '')
  }
  const feature = `form:${label}`
  if (!shouldFire(`${feature}|submit`)) return
  trackUsageAction(feature, 'submit')
}

function handleChange(event: Event, pageFeature: string): void {
  const target = event.target
  if (
    !(
      target instanceof HTMLInputElement ||
      target instanceof HTMLSelectElement ||
      target instanceof HTMLTextAreaElement
    )
  ) {
    return
  }
  const label =
    getStableLabel(target) ||
    target.getAttribute('name')?.replace(/\s+/g, ' ').trim() ||
    target.getAttribute('id')?.replace(/\s+/g, ' ').trim() ||
    target.getAttribute('aria-label')?.replace(/\s+/g, ' ').trim() ||
    pageFeature.replace(/^page:/, '')
  const feature = `field:${label}`
  if (!shouldFire(`${feature}|change`)) return
  trackUsageAction(feature, 'change')
}

export function installUsageActionTracking(getPageFeature: () => string): () => void {
  if (typeof document === 'undefined') {
    return () => undefined
  }

  const clickListener = (event: Event) => {
    try {
      handleClick(event, getPageFeature())
    } catch {
      // Never let tracking throw into the host page.
    }
  }
  const submitListener = (event: Event) => {
    try {
      handleSubmit(event, getPageFeature())
    } catch {
      // ignore
    }
  }
  const changeListener = (event: Event) => {
    try {
      handleChange(event, getPageFeature())
    } catch {
      // ignore
    }
  }

  document.addEventListener('click', clickListener, true)
  document.addEventListener('submit', submitListener, true)
  document.addEventListener('change', changeListener, true)

  return () => {
    document.removeEventListener('click', clickListener, true)
    document.removeEventListener('submit', submitListener, true)
    document.removeEventListener('change', changeListener, true)
  }
}
